import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Add,
  ArrowDropDown,
  ChevronRight,
  Close,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  ExpandMore,
  FileDownload,
  Payment,
  SearchOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DataTable from "components/common/tables/DataTable";
import ApprovalTimeline from "../components/ApprovalTimeline";
import EntityFormDialog from "../components/EntityFormDialog";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import {
  CONTRACT_ITEM_STATUSES,
  DIFFICULTY_LEVELS,
  PRIORITIES,
  UNITS,
} from "constants/projectRules";
import { calculateAmount } from "modules/contracts/utils/commercialCalculations";
import { formatCurrencyWithUnit, formatAmount, formatNumber, formatInputDate } from "utils/formatters";
import { AppButton } from "components/common/buttons";
import { subcontractApi } from "../services/subcontractApi";
import readSubcontractItemsImportExcel from "../utils/subcontract-items/importSubcontractItemsExcel";
import validateSubcontractItemsImport from "../utils/subcontract-items/validateSubcontractItemsImport";
import exportSubcontractItemsExcel from "../utils/subcontract-items/exportSubcontractItemsExcel";
import {
  buildItemHierarchy,
  collectExpandableItemIds,
  compareItemCode,
  flattenAllItems,
  flattenVisibleItemTree,
  getItemCodeLevel,
  getItemHierarchyKey,
  getLeafDescendants,
  hasChildItems,
  sortItemsByCode,
} from "../utils/subcontract-items/itemHierarchy";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";

// const money = (value) => new Intl.NumberFormat("vi-VN").format(value || 0);
const formatQuantity = (value) =>
  new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Number(value || 0));
const normalize = (value) => String(value || "").trim().toUpperCase();
const normalizeSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const priorityLabel = (value) => {
  switch (Number(value)) {
    case 1:
      return "Cao";
    case 2:
      return "Trung bình";
    case 3:
      return "Thấp";
    default:
      return PRIORITIES.find((item) => Number(item.value) === Number(value))?.label || "";
  }
};
const STATUS_OPTIONS = CONTRACT_ITEM_STATUSES.map((item) => item.value);
const STATUS_META = Object.fromEntries(
  CONTRACT_ITEM_STATUSES.map((item) => [item.value, item]),
);
const PRIORITY_OPTIONS = PRIORITIES.map((item) => Number(item.value));
const UNIT_OPTIONS = UNITS.map((item) => item.value);
const DIFFICULTY_OPTIONS = DIFFICULTY_LEVELS.map((item) => Number(item.value));
const normalizeNumberInput = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const rawValue = value.toString().trim();
  let normalized = rawValue;

  if (rawValue.includes(".") && rawValue.includes(",")) {
    normalized = rawValue.replace(/\./g, "").replace(",", ".");
  } else if (rawValue.includes(",")) {
    normalized = rawValue.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(rawValue)) {
    normalized = rawValue.replace(/\./g, "");
  }

  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : Number.NaN;
};
const formatImportErrorValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString("vi-VN");
  if (typeof value === "object") {
    if (value.text !== undefined) return String(value.text);
    if (value.result !== undefined) return String(value.result);
    return JSON.stringify(value);
  }
  return String(value);
};

const getScopeId = (scope) => Number(scope?.subcontractItemScopeId ?? scope?.SubcontractItemScopeId);
const getCommuneId = (commune) => Number(commune?.communeId ?? commune?.CommuneId);
const getCommuneName = (commune) => commune?.communeName ?? commune?.CommuneName ?? commune?.scopeName ?? "";
const isTotalScope = (scope) => normalize(scope?.scopeType) === "TOTAL";
const isCommuneScope = (scope) => normalize(scope?.scopeType) === "COMMUNE";
const isProvinceScope = (scope) => normalize(scope?.scopeType) === "PROVINCE";
const flattenScopes = (sections = []) => sections.flatMap((section) =>
  (section.scopes || []).map((scope) => ({ ...scope, sectionType: section.sectionType })),
);
const flattenSectionItems = (sections = []) => flattenScopes(sections).flatMap((scope) => scope.items || []);
const hasItems = (scope) => (scope?.items || []).length > 0;
const getManualTargetScopes = (sections = []) => flattenScopes(sections).filter(hasItems);
const toOptionalNumber = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);
const calculateUnitPrice = (item = {}) => {
  const laborCount = toOptionalNumber(item.laborCount);
  const laborNorm = toOptionalNumber(item.laborNorm);
  const laborPrice = toOptionalNumber(item.laborPrice);
  return laborCount !== null && laborNorm !== null && laborPrice !== null
    ? laborCount * laborNorm * laborPrice
    : toOptionalNumber(item.unitPrice);
};
const getNextCode = (items = [], parentItem = null) => {
  if (parentItem?.itemCode) {
    const prefix = `${parentItem.itemCode}.`;
    const nextChildIndex = items
      .filter((item) => String(item.parentItemId || "") === String(parentItem.itemId || ""))
      .map((item) => Number(String(item.itemCode || "").slice(prefix.length).split(".")[0]))
      .filter(Number.isFinite)
      .reduce((max, value) => Math.max(max, value), 0) + 1;
    return `${parentItem.itemCode}.${nextChildIndex}`;
  }

  const nextRootIndex = items
    .filter((item) => !item.parentItemId && getItemCodeLevel(item.itemCode) <= 1)
    .map((item) => Number(item.itemCode))
    .filter(Number.isFinite)
    .reduce((max, value) => Math.max(max, value), 0) + 1;
  return String(nextRootIndex);
};
const getCommuneTotalMultiplier = (targetScopes = []) =>
  Math.max(
    targetScopes.filter((scope) => normalize(scope.sectionType) === "COMMUNE_DETAIL" && isCommuneScope(scope)).length,
    1,
  );
const getScopeQuantityMultiplier = (scope, targetScopes = []) =>
  normalize(scope?.sectionType) === "COMMUNE_DETAIL" && isTotalScope(scope)
    ? getCommuneTotalMultiplier(targetScopes)
    : 1;
const findSection = (sections, sectionType) =>
  sections.find((section) => normalize(section.sectionType) === sectionType);
const findScope = (sections, sectionType, predicate) =>
  (findSection(sections, sectionType)?.scopes || []).find(predicate);
const scopeWithSectionType = (scope, sectionType) => scope ? { ...scope, sectionType } : null;
const hasSection = (sections, sectionType) => Boolean(findSection(sections, sectionType));
const requiresCommuneSelection = (sections = []) =>
  hasSection(sections, "COMMUNE_DETAIL") || hasSection(sections, "PROVINCE_COMMUNE");
const getImportScopeKey = (scope) => [
  normalize(scope?.sectionType),
  normalize(scope?.scopeType),
  String(scope?.communeId ?? scope?.CommuneId ?? ""),
].join(":");

const aggregateCommuneItems = (scopes) => {
  const communeItems = scopes
    .filter((scope) => isCommuneScope(scope))
    .flatMap((scope) => scope.items || []);
  const groups = new Map();

  communeItems.forEach((item) => {
    // Cùng mã/tên vẫn là hai dòng nghiệp vụ khác nhau khi mức khó khăn khác nhau.
    const key = `${item.itemCode || ""}|${item.itemName || ""}|${item.difficultyLevel ?? ""}|${item.unit || ""}|${item.parentItemCode || ""}`;
    const current = groups.get(key) || {
      ...item,
      itemId: `total-${key}`,
      subcontractItemScopeId: null,
      subcontractQuantity: 0,
      subcontractAmount: 0,
      completionPercentage: 0,
      _count: 0,
      isCalculatedTotal: true,
    };
    current.subcontractQuantity += Number(item.subcontractQuantity || 0);
    current.subcontractAmount += Number(item.subcontractAmount || calculateAmount(item.subcontractQuantity, item.unitPrice));
    current.completionPercentage += Number(item.completionPercentage || 0);
    current._count += 1;
    groups.set(key, current);
  });

  return [...groups.values()].map((item) => ({
    ...item,
    completionPercentage: item._count ? item.completionPercentage / item._count : 0,
  })).sort(compareItemCode);
};

const templateFileUrl = `${process.env.PUBLIC_URL || ""}/mau_import_hang_muc_quyet_dinh_giao_khoan.xlsx`;

export default function ItemsTab({ subcontractId, subcontract = {}, sections = [], payments = [], approvals = [], onChanged }) {
  const permissions = useSubcontractPermissions();
  const [sectionIndex, setSectionIndex] = useState(0);
  const [scopeIndex, setScopeIndex] = useState(0);
  const [dialog, setDialog] = useState(null);
  const [inlineSaving, setInlineSaving] = useState(false);
  const [tableNotice, setTableNotice] = useState(null);
  const [selectedDetailItemKey, setSelectedDetailItemKey] = useState(null);
  const [selectedCommuneKeys, setSelectedCommuneKeys] = useState([]);
  const [communePickerAnchor, setCommunePickerAnchor] = useState(null);
  const [communeDraftKeys, setCommuneDraftKeys] = useState([]);
  const [communeSearch, setCommuneSearch] = useState("");
  const [communes, setCommunes] = useState([]);
  const [importDialog, setImportDialog] = useState({
    open: false,
    file: null,
    fileName: "",
    result: null,
    checking: false,
    importing: false,
    error: "",
  });
  const fileInputRef = useRef(null);

  const activeSection = sections[sectionIndex];
  const scopes = useMemo(() => activeSection?.scopes || [], [activeSection]);
  const activeScope = scopes[Math.min(scopeIndex, Math.max(scopes.length - 1, 0))];
  const isCommuneTotal = normalize(activeSection?.sectionType) === "COMMUNE_DETAIL" && isTotalScope(activeScope);
  const displayedItemTree = useMemo(
    () => buildItemHierarchy(isCommuneTotal ? aggregateCommuneItems(scopes) : (activeScope?.items || [])),
    [activeScope, isCommuneTotal, scopes],
  );
  const [expandedItemIds, setExpandedItemIds] = useState(new Set());
  const allItems = useMemo(() => flattenSectionItems(sections), [sections]);
  const allPaymentItems = useMemo(() => payments.flatMap((profile) => profile.items || []), [payments]);
  const hasAnyItem = allItems.length > 0;
  const activeScopeHasItems = hasItems(activeScope);
  const canEdit = false;
  const canViewPaymentInfo = permissions.has(SUBCONTRACT_PERMISSIONS.paymentRead);
  const isParentItem = useCallback((item) => hasChildItems(item), []);

  useEffect(() => {
    setExpandedItemIds(new Set(collectExpandableItemIds(displayedItemTree)));
  }, [displayedItemTree]);

  const displayedItems = useMemo(
    () => flattenVisibleItemTree(displayedItemTree, expandedItemIds),
    [displayedItemTree, expandedItemIds],
  );

  const handleToggleExpand = useCallback((item) => {
    const itemKey = getItemHierarchyKey(item);
    setExpandedItemIds((current) => {
      const next = new Set(current);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  }, []);

  const getDetailItemKey = useCallback(
    (item) => String(item?.itemId ?? `${item?.itemCode || ""}-${item?.itemName || ""}`),
    [],
  );

  const displayedItemsFlat = useMemo(
    () => flattenAllItems(displayedItemTree),
    [displayedItemTree],
  );

  const selectedDetailItem = useMemo(
    () => displayedItemsFlat.find((item) => getDetailItemKey(item) === selectedDetailItemKey) || null,
    [displayedItemsFlat, getDetailItemKey, selectedDetailItemKey],
  );

  useEffect(() => {
    if (selectedDetailItemKey && !selectedDetailItem) {
      setSelectedDetailItemKey(null);
    }
  }, [selectedDetailItem, selectedDetailItemKey]);

  const resolveSourceLeafItems = useCallback((item) => {
    if (!item) return [];

    const leafItems = hasChildItems(item) ? getLeafDescendants(item) : [item];
    const actualLeafItems = allItems.filter((candidate) => !hasChildItems(candidate));
    const byId = new Map(actualLeafItems.map((candidate) => [String(candidate.itemId), candidate]));
    const resolved = [];
    const seen = new Set();

    leafItems.forEach((leaf) => {
      const directMatch = byId.get(String(leaf.itemId));
      const matchedItems = directMatch
        ? [directMatch]
        : actualLeafItems.filter((candidate) =>
          String(candidate.itemCode || "") === String(leaf.itemCode || "") &&
          String(candidate.itemName || "") === String(leaf.itemName || "") &&
          String(candidate.difficultyLevel ?? "") === String(leaf.difficultyLevel ?? ""),
        );

      matchedItems.forEach((matchedItem) => {
        const key = String(matchedItem.itemId);
        if (!seen.has(key)) {
          seen.add(key);
          resolved.push(matchedItem);
        }
      });
    });

    return resolved;
  }, [allItems]);

  const getPaymentTargetItemId = useCallback(
    (payment) => Number(payment?.itemId ?? payment?.ItemId ?? payment?.subcontractItemId ?? payment?.SubcontractItemId),
    [],
  );

  const getPaymentAmount = useCallback((item, payment) => {
    const explicitAmount = Number(payment?.paymentAmount ?? payment?.PaymentAmount ?? 0);
    if (explicitAmount > 0) {
      return Math.round(explicitAmount);
    }

    const quantity = Number(payment?.paymentQuantity ?? payment?.PaymentQuantity ?? 0);
    return Math.round((Number(item?.unitPrice) || 0) * quantity);
  }, []);

  const getItemPaymentSummary = useCallback((item) => {
    const sourceItems = resolveSourceLeafItems(item);
    const sourceIds = new Set(sourceItems.map((sourceItem) => Number(sourceItem.itemId)));
    const relatedPayments = allPaymentItems.filter((payment) => sourceIds.has(getPaymentTargetItemId(payment)));
    const beforeTax = relatedPayments.reduce((total, payment) => {
      const sourceItem = sourceItems.find((candidate) => Number(candidate.itemId) === getPaymentTargetItemId(payment));
      return total + getPaymentAmount(sourceItem, payment);
    }, 0);
    const afterTax = relatedPayments.reduce((total, payment) => {
      const sourceItem = sourceItems.find((candidate) => Number(candidate.itemId) === getPaymentTargetItemId(payment));
      const profile = payments.find(
        (paymentProfile) => Number(paymentProfile.paymentProfileId) === Number(payment.paymentProfileId ?? payment.PaymentProfileId),
      );
      const vatRate = (Number(profile?.vat ?? subcontract?.vat) || 0) / 100;
      return total + Math.round(getPaymentAmount(sourceItem, payment) * (1 + vatRate));
    }, 0);
    const baseAmount = Number(item?.subcontractAmount)
      || sourceItems.reduce((total, sourceItem) => total + (Number(sourceItem.subcontractAmount) || 0), 0);
    const progress = baseAmount > 0 ? Math.min((beforeTax / baseAmount) * 100, 100) : 0;

    return {
      beforeTax,
      afterTax,
      progress,
      payments: relatedPayments,
      sourceItems,
    };
  }, [allPaymentItems, getPaymentAmount, getPaymentTargetItemId, payments, resolveSourceLeafItems, subcontract?.vat]);

  const renderCompactField = (label, value, options = {}) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.68rem", fontWeight: 650, color: "#64748b" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "0.78rem",
          fontWeight: options.strong || options.money ? 750 : 500,
          color: "#111827",
          mt: 0.25,
          wordBreak: "break-word",
        }}
      >
        {value === null || value === undefined || value === "" ? "-" : value}
      </Typography>
    </Box>
  );

  const handleOpenDetailPanel = useCallback((item) => {
    setSelectedDetailItemKey(getDetailItemKey(item));
  }, [getDetailItemKey]);

  const handleCloseDetailPanel = useCallback(() => {
    setSelectedDetailItemKey(null);
  }, []);

  const isInlineCellEditable = (item, field) => {
    if (!item || item.isCalculatedTotal) return false;
    if (!["itemName", "unit", "difficultyLevel", "subcontractQuantity", "laborCount", "laborNorm", "laborPrice", "priority", "status"].includes(field)) {
      return false;
    }

    if (isParentItem(item) && !["itemName", "unit", "priority"].includes(field)) {
      return false;
    }

    return true;
  };
  const normalizeInlineEditValue = (field, value) => {
    if (field === "subcontractQuantity" || field === "unitPrice" || field === "laborCount" || field === "laborNorm" || field === "laborPrice") {
      return normalizeNumberInput(value);
    }

    if (field === "difficultyLevel" || field === "priority") {
      const numberValue = Number(value);
      return Number.isInteger(numberValue) ? numberValue : Number.NaN;
    }

    if (field === "itemName" || field === "unit" || field === "status") {
      return value === null || value === undefined ? "" : value.toString().trim();
    }

    return value;
  };
  const validateInlineEditValue = (field, value) => {
    if (field === "subcontractQuantity" || field === "unitPrice" || field === "laborCount" || field === "laborNorm" || field === "laborPrice") {
      if (!Number.isFinite(value) || value < 0) {
        if (field === "subcontractQuantity") return "Khối lượng phải là số hợp lệ và không âm";
        if (field === "unitPrice") return "Đơn giá phải là số hợp lệ và không âm";
        if (field === "laborPrice") return "Đơn giá công phải là số hợp lệ và không âm";
        return "Giá trị phải là số hợp lệ và không âm";
      }
    }

    if (field === "difficultyLevel" && !DIFFICULTY_OPTIONS.includes(value)) {
      return "Mức độ khó khăn không hợp lệ";
    }

    if (field === "priority" && !PRIORITY_OPTIONS.includes(value)) {
      return "Độ ưu tiên không hợp lệ";
    }

    if (field === "unit" && value && !UNIT_OPTIONS.includes(value)) {
      return "Đơn vị tính không hợp lệ";
    }

    if (field === "status" && value && !STATUS_OPTIONS.includes(value)) {
      return "Trạng thái không hợp lệ";
    }

    if (field === "itemName" && !value) {
      return "Tên dự toán không được để trống";
    }

    return "";
  };
  const getStatusChipStyles = (status) => {
    const meta = STATUS_META[status];
    const color = meta?.color;
    return {
      color: color?.color || "#5f6b7a",
      borderColor: color?.border || "#cbd2d9",
      backgroundColor: color?.background || "#eef1f4",
      "& .MuiChip-label": {
        fontFamily: "Inter, sans-serif",
        fontSize: "0.58rem",
        fontWeight: 600,
      },
    };
  };
  const handleInlineEdit = async ({ item, field, value }) => {
    if (!isInlineCellEditable(item, field)) return;

    const nextValue = normalizeInlineEditValue(field, value);
    const validationMessage = validateInlineEditValue(field, nextValue);
    if (validationMessage) {
      setTableNotice({ severity: "warning", message: validationMessage });
      return;
    }

    const currentValue = normalizeInlineEditValue(field, item[field]);
    if (currentValue === nextValue) return;

    try {
      setInlineSaving(true);
      setTableNotice(null);

      const nextItem = {
        ...item,
        [field]: nextValue,
      };
      const quantity = nextItem.subcontractQuantity !== null && nextItem.subcontractQuantity !== undefined
        ? Number(nextItem.subcontractQuantity)
        : null;
      const price = isParentItem(nextItem) ? null : calculateUnitPrice(nextItem);

      const submitData = {
        subcontractId: Number(subcontractId),
        subcontractItemScopeId: nextItem.subcontractItemScopeId || getScopeId(activeScope),
        itemCode: nextItem.itemCode || "",
        itemName: nextItem.itemName || "",
        description: nextItem.description || "",
        unit: nextItem.unit || "",
        subcontractQuantity: quantity,
        unitPrice: price,
        subcontractAmount: quantity !== null && price !== null ? quantity * price : null,
        laborCount: nextItem.laborCount === null || nextItem.laborCount === undefined ? null : Number(nextItem.laborCount),
        laborNorm: nextItem.laborNorm === null || nextItem.laborNorm === undefined ? null : Number(nextItem.laborNorm),
        laborPrice: nextItem.laborPrice === null || nextItem.laborPrice === undefined ? null : Number(nextItem.laborPrice),
        parentItemId: nextItem.parentItemId || null,
        startDate: nextItem.startDate || null,
        endDate: nextItem.endDate || null,
        status: nextItem.status || "Chưa bắt đầu",
        priority: nextItem.priority ? Number(nextItem.priority) : 2,
        completionPercentage: nextItem.completionPercentage || 0,
        difficultyLevel: nextItem.difficultyLevel ? Number(nextItem.difficultyLevel) : 1,
        notes: nextItem.notes || "",
        level_: nextItem.level_ ?? Math.max(getItemCodeLevel(nextItem.itemCode) - 1, 0),
      };

      const response = await subcontractApi.updateItem(nextItem.itemId, submitData);
      if (response?.success === false || response?.Success === false) {
        setTableNotice({
          severity: "error",
          message: response?.message || response?.Message || "Lỗi khi cập nhật dự toán giao khoán",
        });
        return;
      }

      setTableNotice({ severity: "success", message: "Cập nhật dự toán thành công" });
      await onChanged?.();
    } catch (error) {
      setTableNotice({
        severity: "error",
        message: error?.response?.data?.message || error?.message || "Lỗi khi cập nhật dự toán giao khoán",
      });
    } finally {
      setInlineSaving(false);
    }
  };
  const getInlineEditTextFieldProps = ({ cell, row, table }) => {
    const field = cell.column.id;
    const savesOnChange =
      field === "status" || field === "difficultyLevel" || field === "unit" || field === "priority";

    return {
      size: "small",
      variant: "standard",
      autoFocus: true,
      onChange: savesOnChange
        ? (event) => {
          handleInlineEdit({
            item: row.original,
            field,
            value: event.target.value,
          });
          window.setTimeout(() => table.setEditingCell(null), 0);
        }
        : undefined,
      onBlur: savesOnChange
        ? undefined
        : (event) => {
          handleInlineEdit({
            item: row.original,
            field,
            value: event.target.value,
          });
        },
      onKeyDown: (event) => {
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) {
          event.stopPropagation();
          return;
        }

        if (event.key === "Enter" && !savesOnChange) {
          handleInlineEdit({
            item: row.original,
            field,
            value: event.currentTarget.value,
          });
          table.setEditingCell(null);
        }

        if (event.key === "Escape") {
          table.setEditingCell(null);
        }
      },
      inputProps: {
        style: {
          fontFamily: "Inter, sans-serif",
          fontSize: "0.65rem",
          textAlign: field === "subcontractQuantity" || field === "unitPrice" || field === "laborCount" || field === "laborNorm" || field === "laborPrice" ? "right" : "left",
        },
      },
      SelectProps: {
        MenuProps: {
          PaperProps: {
            sx: {
              maxHeight: 280,
              "& .MuiMenuItem-root": {
                fontFamily: "Inter, sans-serif",
                fontSize: "0.75rem",
              },
            },
          },
        },
      },
    };
  };
  const provinceId = subcontract?.provinceId ?? subcontract?.ProvinceId;
  const communeOptions = useMemo(() => communes.map((commune) => ({
    communeId: getCommuneId(commune),
    communeName: getCommuneName(commune),
  })).filter((commune) => commune.communeId), [communes]);
  const selectedCommuneNames = useMemo(() => {
    const selected = new Set(selectedCommuneKeys);
    return communeOptions
      .filter((commune) => selected.has(String(commune.communeId)))
      .map((commune) => commune.communeName);
  }, [communeOptions, selectedCommuneKeys]);
  const filteredCommuneOptions = useMemo(() => {
    const search = normalizeSearch(communeSearch);
    if (!search) return communeOptions;
    return communeOptions.filter((commune) =>
      normalizeSearch(commune.communeName).includes(search),
    );
  }, [communeOptions, communeSearch]);
  const expectedImportScopes = useMemo(() => {
    const selected = new Set(selectedCommuneKeys.map(String));
    const selectedCommunes = communeOptions.filter((commune) =>
      selected.has(String(commune.communeId)),
    );
    const result = [];

    if (hasSection(sections, "ESTIMATE")) {
      result.push({
        sectionType: "ESTIMATE",
        scopeType: "ESTIMATE",
        sheetName: "Dự toán đơn",
      });
    }

    if (hasSection(sections, "PROVINCE_COMMUNE")) {
      result.push({
        sectionType: "PROVINCE_COMMUNE",
        scopeType: "PROVINCE",
        sheetName: subcontract?.provinceName || subcontract?.ProvinceName || "",
      });
    }

    ["COMMUNE_DETAIL", "PROVINCE_COMMUNE"].forEach((sectionType) => {
      if (!hasSection(sections, sectionType)) return;
      selectedCommunes.forEach((commune) => {
        result.push({
          sectionType,
          scopeType: "COMMUNE",
          communeId: commune.communeId,
          sheetName: commune.communeName,
        });
      });
    });

    return result;
  }, [communeOptions, sections, selectedCommuneKeys, subcontract]);
  const importScopeNames = useMemo(
    () => expectedImportScopes.map((scope) => scope.sheetName).filter(Boolean),
    [expectedImportScopes],
  );
  const parentOptions = useMemo(
    () => sortItemsByCode(activeScope?.items || [])
      .filter((item) => getItemCodeLevel(item.itemCode) < 4)
      .map((item) => ({
        value: item.itemId,
        label: `${item.itemCode || ""} - ${item.itemName || ""}${item.difficultyLevel == null ? "" : ` (KK ${item.difficultyLevel})`}`,
      })),
    [activeScope],
  );

  const hiddenFieldsForParent = useMemo(() => new Set([
    "unit",
    "difficultyLevel",
    "subcontractQuantity",
    "unitPrice",
    "laborCount",
    "laborNorm",
    "laborPrice",
    "status",
    "completionPercentage",
  ]), []);

  const columns = useMemo(() => [
    {
      accessorKey: "itemCode",
      header: "STT",
      size: 55,
      // minSize: 100,
      maxSize: 55,
      Cell: ({ cell, row }) => {
        const item = row.original;
        const level = item.level_ || 0;

        return (
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: 24,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {hasChildItems(item) ? (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleToggleExpand(item);
                  }}
                >
                  {expandedItemIds.has(getItemHierarchyKey(item)) ? (
                    <ExpandMore sx={{ fontSize: "0.9rem" }} />
                  ) : (
                    <ChevronRight sx={{ fontSize: "0.9rem" }} />
                  )}
                </IconButton>
              ) : null}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "11px",
                fontWeight: level === 0 ? 600 : 400,
              }}
            >
              {cell.getValue() || ""}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "itemName",
      header: "Dự toán",
      size: 300,
      minSize: 170,
      maxSize: 350,
      enableEditing: (row) => isInlineCellEditable(row.original, "itemName"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
      Cell: ({ cell, row }) => {
        const item = row.original;
        const level = item.level_ || 0;

        return (
          <Typography
            variant="body2"
            sx={{
              pl: `${Math.min(level, 4) * 14}px`,
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
              fontWeight: level === 0 ? 600 : 400,
            }}
          >
            {cell.getValue() || "-"}
          </Typography>
        )
      }
    },
    {
      accessorKey: "unit", header: "ĐVT", size: 40,
      enableEditing: (row) => isInlineCellEditable(row.original, "unit"),
      editVariant: "select",
      editSelectOptions: UNIT_OPTIONS.map((value) => ({
        value,
        label: value.toString(),
      })),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
            }}
          >
            {cell.getValue() || ""}
          </Typography>
        );
      },
    },
    {
      accessorKey: "difficultyLevel", header: "Mức KK", size: 50,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "difficultyLevel"),
      editVariant: "select",
      editSelectOptions: DIFFICULTY_OPTIONS.map((value) => ({
        value,
        label: value.toString(),
      })),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        const item = row.original;
        // Nếu là dự toán cha (có children), không hiển thị
        if (isParentItem(item)) {
          return null;
        }

        const level = cell.getValue();

        // Nếu không có dữ liệu thực thi thực sự, ẩn các trường mặc định
        const hasExecutionData =
          item.contractQuantity && item.unitPrice && item.contractAmount;

        // Ẩn difficultyLevel nếu:
        // 1. Không có dữ liệu thực thi và giá trị = 1 (mặc định)
        // 2. Giá trị null/undefined/0
        if (
          level === null ||
          level === undefined ||
          level === 0
        ) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.45rem",
                color: "text.secondary",
              }}
            ></Typography>
          );
        }

        const getDifficultyLabel = (level) => {
          return level.toString();
        };

        const getDifficultyColor = (level) => {
          switch (level) {
            case 1:
              return "#4caf50"; // Xanh lá
            case 2:
              return "#8bc34a"; // Xanh lá nhạt
            case 3:
              return "#ff9800"; // Cam
            case 4:
              return "#f44336"; // Đỏ
            case 5:
              return "#9c27b0"; // Tím
            default:
              return "#9e9e9e"; // Xám
          }
        };

        return (
          <Chip
            label={getDifficultyLabel(level)}
            size="small"
            variant="outlined"
            sx={{
              borderColor: getDifficultyColor(level),
              color: getDifficultyColor(level),
              fontFamily: "Inter, sans-serif",
              fontSize: "0.525rem",
              height: "20px",
              "& .MuiChip-label": {
                padding: "0 6px",
              },
            }}
          />
        );
      },
    },
    {
      accessorKey: "laborCount", header: "Định biên", size: 60,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "laborCount"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        if (isParentItem(row.original)) return null;
        const value = cell.getValue();
        return value === null || value === undefined || value === "" ? "" : formatQuantity(value);
      },
    },
    {
      accessorKey: "laborNorm", header: "Định mức", size: 60,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "laborNorm"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        if (isParentItem(row.original)) return null;
        const value = cell.getValue();
        return value === null || value === undefined || value === "" ? "" : formatQuantity(value);
      },
    },
    {
      accessorKey: "laborPrice", header: "Đơn giá công", size: 70,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "laborPrice"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        if (isParentItem(row.original)) return null;
        return cell.getValue()
          ? formatAmount(cell.getValue()).replace(/\s*₫\s*$/, "")
          : "";
      },
    },
    {
      accessorKey: "unitPrice", header: "Đơn giá sản phẩm", size: 70,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "unitPrice"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        const item = row.original;
        // Nếu là dự toán cha (có children), không hiển thị
        if (isParentItem(item)) {
          return null;
        }

        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
            }}
          >
            {cell.getValue()
              ? formatAmount(cell.getValue()).replace(/\s*₫\s*$/, "")
              : ""}
          </Typography>
        );
      },
    },
    {
      accessorKey: "subcontractQuantity", header: "Khối lượng", size: 50,
      enableEditing: (row) =>
        isInlineCellEditable(row.original, "subcontractQuantity"),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell, row }) => {
        const item = row.original;
        // Nếu là dự toán cha (có children), không hiển thị
        if (isParentItem(item)) {
          return null;
        }

        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
            }}
          >
            {cell.getValue() === null || cell.getValue() === undefined || cell.getValue() === ""
              ? ""
              : formatQuantity(cell.getValue())}
          </Typography>
        );
      },
    },
    {
      accessorKey: "subcontractAmount", header: "Thành tiền", size: 110,
      enableEditing: false,
      muiTableBodyCellProps: () => {
        return {
          align: 'right',
        };
      },
      Cell: ({ cell, row }) => {
        const item = row.original;
        const level = item.level_ || 0;

        return (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "11px",
              fontWeight: level === 0 ? 600 : 400,
              color: level === 0 ? "#D36135" : "inherit"
            }}
          >
            {formatAmount(cell.getValue())}
          </Typography>
        );
      },
    },
    {
      accessorKey: "priority", header: "Độ ưu tiên", size: 80,
      enableEditing: (row) => isInlineCellEditable(row.original, "priority"),
      editVariant: "select",
      editSelectOptions: PRIORITIES.map((item) => ({
        value: Number(item.value),
        label: priorityLabel(item.value),
      })),
      muiEditTextFieldProps: getInlineEditTextFieldProps,
      Cell: ({ cell }) => priorityLabel(cell.getValue()),
    },
    {
      accessorKey: "completionPercentage",
      header: "Tiến độ",
      size: 60,
      enableEditing: false,
      Cell: ({ cell }) => (
        <Box sx={{ minWidth: 110 }}>
          <Typography fontSize={11}>{formatNumber(cell.getValue())}%</Typography>
          <LinearProgress variant="determinate" value={Number(cell.getValue() || 0)} sx={{ height: 6, borderRadius: 3 }} />
        </Box>
      ),
    },
    { accessorKey: "status", header: "Trạng thái", size: 60, Cell: ({ cell }) => <StatusChip value={cell.getValue()} /> },
  ].map((column) => {
    if (column.accessorKey === "priority") {
      return {
        ...column,
        Cell: ({ cell, row }) => {
          if (isParentItem(row.original)) {
            return null;
          }

          const priority = Number(cell.getValue());
          if (!priority) return null;

          const getPriorityColor = (value) => {
            switch (value) {
              case 1:
                return "#f44336";
              case 2:
                return "#ff9800";
              case 3:
                return "#4caf50";
              default:
                return "#9e9e9e";
            }
          };

          return (
            <Chip
              label={priorityLabel(priority)}
              size="small"
              variant="outlined"
              sx={{
                borderColor: getPriorityColor(priority),
                color: getPriorityColor(priority),
                "& .MuiChip-label": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.58rem",
                  fontWeight: 600,
                },
              }}
            />
          );
        },
      };
    }

    if (column.accessorKey === "status") {
      return {
        ...column,
        enableEditing: (row) => isInlineCellEditable(row.original, "status"),
        editVariant: "select",
        editSelectOptions: STATUS_OPTIONS,
        muiEditTextFieldProps: getInlineEditTextFieldProps,
        Cell: ({ cell, row }) => {
          if (isParentItem(row.original)) {
            return null;
          }

          const status = cell.getValue();
          if (!status) return null;

          return (
            <Chip
              label={status}
              size="small"
              variant="outlined"
              sx={getStatusChipStyles(status)}
            />
          );
        },
      };
    }

    if (!hiddenFieldsForParent.has(column.accessorKey)) {
      return column;
    }

    const originalCell = column.Cell;
    return {
      ...column,
      Cell: (props) => {
        if (hasChildItems(props.row?.original)) {
          return null;
        }

        return originalCell ? originalCell(props) : (props.cell?.getValue() ?? "");
      },
    };
  }), [
    expandedItemIds,
    getInlineEditTextFieldProps,
    getStatusChipStyles,
    handleToggleExpand,
    hiddenFieldsForParent,
    isInlineCellEditable,
    isParentItem,
  ]);

  const saveItem = async (form) => {
    const parentInActiveScope = form.parentItemId
      ? (activeScope?.items || []).find((item) => String(item.itemId) === String(form.parentItemId))
      : null;
    const formIsParent = isParentItem(form);
    const itemCode = form.itemCode || getNextCode(activeScope?.items || [], parentInActiveScope);
    const unitPrice = formIsParent ? null : calculateUnitPrice(form);
    const basePayload = {
      ...form,
      subcontractId: Number(subcontractId),
      itemCode,
      parentItemId: form.parentItemId || null,
      subcontractQuantity: form.subcontractQuantity === "" || form.subcontractQuantity === null || form.subcontractQuantity === undefined ? 0 : Number(form.subcontractQuantity),
      unitPrice,
      subcontractAmount: formIsParent ? null : calculateAmount(form.subcontractQuantity || 0, unitPrice),
      laborCount: form.laborCount === "" || form.laborCount === null || form.laborCount === undefined ? null : Number(form.laborCount),
      laborNorm: form.laborNorm === "" || form.laborNorm === null || form.laborNorm === undefined ? null : Number(form.laborNorm),
      laborPrice: form.laborPrice === "" || form.laborPrice === null || form.laborPrice === undefined ? null : Number(form.laborPrice),
      priority: form.priority === "" ? null : Number(form.priority),
      difficultyLevel: form.difficultyLevel === "" ? null : Number(form.difficultyLevel),
      level_: Math.max(getItemCodeLevel(itemCode) - 1, 0),
      startDate: form.startDate || null,
      endDate: form.endDate || null,
    };

    if (dialog?.data?.itemId) {
      await subcontractApi.updateItem(dialog.data.itemId, {
        ...basePayload,
        subcontractItemScopeId: getScopeId(activeScope),
      });
    } else {
      const targetScopes = getManualTargetScopes(sections);
      await subcontractApi.createItemBatch({
        ...basePayload,
        scopes: targetScopes.map((scope) => ({
          subcontractItemScopeId: getScopeId(scope),
          parentItemId: parentInActiveScope
            ? (scope.items || []).find((item) =>
              item.itemCode === parentInActiveScope.itemCode &&
              String(item.difficultyLevel ?? "") === String(parentInActiveScope.difficultyLevel ?? ""),
            )?.itemId || null
            : null,
          quantityMultiplier: getScopeQuantityMultiplier(scope, targetScopes),
        })),
      });
    }
    await onChanged?.();
  };

  useEffect(() => {
    let active = true;
    const loadCommunes = async () => {
      if (!provinceId) {
        setCommunes([]);
        return;
      }
      try {
        const result = await subcontractApi.getCommunes({ provinceId });
        if (active) setCommunes(result.data || []);
      } catch {
        if (active) setCommunes([]);
      }
    };
    loadCommunes();
    return () => { active = false; };
  }, [provinceId]);

  const openImportDialog = () => {
    // setSelectedCommuneKeys(communeOptions.map((commune) => String(commune.communeId)));
    // setCommuneDraftKeys(communeOptions.map((commune) => String(commune.communeId)));
    setSelectedCommuneKeys([]);
    setCommuneDraftKeys([]);
    setCommuneSearch("");
    setCommunePickerAnchor(null);
    setImportDialog({ open: true, file: null, fileName: "", result: null, checking: false, importing: false, error: "" });
  };

  const openCommunePicker = (event) => {
    setCommuneDraftKeys(selectedCommuneKeys);
    setCommunePickerAnchor(event.currentTarget);
  };

  const closeCommunePicker = () => {
    setCommunePickerAnchor(null);
    setCommuneSearch("");
  };

  const toggleCommuneDraft = (communeKey) => {
    setCommuneDraftKeys((current) =>
      current.includes(communeKey)
        ? current.filter((key) => key !== communeKey)
        : [...current, communeKey],
    );
  };

  const applyCommuneSelection = () => {
    setSelectedCommuneKeys(communeDraftKeys);
    setImportDialog((current) => ({ ...current, result: null, error: "" }));
    closeCommunePicker();
  };

  const resetCommuneSelection = () => {
    setCommuneDraftKeys([]);
    setSelectedCommuneKeys([]);
    setImportDialog((current) => ({ ...current, result: null, error: "" }));
    setCommuneSearch("");
  };

  const ensureSelectedImportScopes = async () => {
    const selected = new Set(selectedCommuneKeys.map(String));
    const selectedCommunes = communeOptions.filter((commune) => selected.has(String(commune.communeId)));
    const targetScopes = [];
    const provinceScope = scopeWithSectionType(findScope(sections, "PROVINCE_COMMUNE", isProvinceScope), "PROVINCE_COMMUNE");
    const estimateScope = scopeWithSectionType(findSection(sections, "ESTIMATE")?.scopes?.[0], "ESTIMATE");
    const communeDetailSection = findSection(sections, "COMMUNE_DETAIL");
    const provinceCommuneSection = findSection(sections, "PROVINCE_COMMUNE");

    if (estimateScope) targetScopes.push(estimateScope);
    if (provinceScope) targetScopes.push(provinceScope);

    if (requiresCommuneSelection(sections) && selectedCommunes.length === 0) {
      throw new Error("Vui lòng chọn xã/phường để import dự toán.");
    }

    if (provinceCommuneSection && !provinceScope) {
      throw new Error("Chưa có phạm vi cấp tỉnh cho phân cấp tỉnh - xã.");
    }

    // Trước lần import đầu, COMMUNE_DETAIL chỉ có scope TOTAL dùng để tính tổng hợp.
    // Các scope xã/phường được chọn sẽ được tạo bên dưới nên đây vẫn là cấu trúc hợp lệ.
    if (targetScopes.length === 0 && !communeDetailSection) {
      throw new Error("Chưa có cấu trúc dự toán để import.");
    }

    const ensuredCommuneScopes = await Promise.all(selectedCommunes.flatMap((commune, index) => {
      const existingCommuneDetailScope = scopeWithSectionType(
        findScope(sections, "COMMUNE_DETAIL", (scope) =>
          isCommuneScope(scope) && String(scope.communeId ?? scope.CommuneId) === String(commune.communeId),
        ),
        "COMMUNE_DETAIL",
      );
      const existingProvinceCommuneScope = scopeWithSectionType(
        findScope(sections, "PROVINCE_COMMUNE", (scope) =>
          isCommuneScope(scope) && String(scope.communeId ?? scope.CommuneId) === String(commune.communeId),
        ),
        "PROVINCE_COMMUNE",
      );
      const scopesToEnsure = [];

      if (communeDetailSection) {
        scopesToEnsure.push(existingCommuneDetailScope || subcontractApi.createScope({
          subcontractSectionId: communeDetailSection.subcontractSectionId,
          scopeType: "COMMUNE",
          provinceId,
          communeId: commune.communeId,
          scopeName: commune.communeName,
          isSystemGenerated: true,
          isEditable: true,
          displayOrder: index + 1,
        }).then((scope) => ({ ...scope, sectionType: "COMMUNE_DETAIL" })));
      }

      if (provinceCommuneSection) {
        scopesToEnsure.push(existingProvinceCommuneScope || subcontractApi.createScope({
          subcontractSectionId: provinceCommuneSection.subcontractSectionId,
          scopeType: "COMMUNE",
          provinceId,
          communeId: commune.communeId,
          scopeName: commune.communeName,
          isSystemGenerated: true,
          isEditable: true,
          displayOrder: index + 1,
        }).then((scope) => ({ ...scope, sectionType: "PROVINCE_COMMUNE" })));
      }

      return scopesToEnsure;
    }));

    return [
      ...targetScopes,
      ...ensuredCommuneScopes.flat().filter(Boolean),
    ];
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportDialog((current) => ({ ...current, file, fileName: file.name, error: "", result: null }));
  };

  const checkImportFile = async () => {
    if (requiresCommuneSelection(sections) && selectedCommuneKeys.length === 0) {
      setImportDialog((current) => ({ ...current, error: "Chưa chọn phường/xã." }));
      return;
    }

    if (!importDialog.file) {
      setImportDialog((current) => ({ ...current, error: "Vui lòng chọn file Excel trước khi kiểm tra." }));
      return;
    }

    setImportDialog((current) => ({ ...current, checking: true, error: "", result: null }));
    try {
      if (expectedImportScopes.some((scope) => !scope.sheetName)) {
        throw new Error("Chưa xác định được tên tỉnh/thành phố để đối chiếu tên sheet.");
      }
      const importData = await readSubcontractItemsImportExcel(importDialog.file);
      const result = validateSubcontractItemsImport({ importData, expectedScopes: expectedImportScopes });
      setImportDialog((current) => ({
        ...current,
        result,
        checking: false,
        error: result.isValid ? "" : "File có lỗi. Vui lòng xem bảng chi tiết bên dưới.",
      }));
    } catch (error) {
      setImportDialog((current) => ({
        ...current,
        error: error.message || "Không thể đọc file Excel.",
        checking: false,
      }));
    }
  };

  const submitImport = async () => {
    if (!importDialog.result?.isValid) return;
    if (requiresCommuneSelection(sections) && selectedCommuneKeys.length === 0) {
      setImportDialog((current) => ({ ...current, error: "Chưa có phạm vi dữ liệu để import." }));
      return;
    }

    setImportDialog((current) => ({ ...current, importing: true, error: "" }));
    try {
      const targetScopes = await ensureSelectedImportScopes();
      const targetScopeByKey = new Map(targetScopes.map((scope) => [getImportScopeKey(scope), scope]));
      const scopePayloads = importDialog.result.scopes.map((scopeResult) => {
        const targetScope = targetScopeByKey.get(getImportScopeKey(scopeResult));
        if (!targetScope) {
          throw new Error(`Không tạo được phạm vi import cho sheet "${scopeResult.sheetName}".`);
        }
        return {
          subcontractItemScopeId: getScopeId(targetScope),
          sheetName: scopeResult.sheetName,
          items: scopeResult.items,
        };
      });
      await subcontractApi.importItemsBatch({
        subcontractId: Number(subcontractId),
        scopes: scopePayloads,
      });
      setImportDialog({ open: false, file: null, fileName: "", result: null, checking: false, importing: false, error: "" });
      await onChanged?.();
    } catch (error) {
      setImportDialog((current) => ({
        ...current,
        importing: false,
        error: error.response?.data?.message || error.message || "Không thể import dự toán.",
      }));
    }
  };

  const exportItems = async () => {
    await exportSubcontractItemsExcel({
      sections,
      subcontract,
      paymentProfiles: payments,
      subcontractPayments: payments.flatMap((profile) => profile.items || []),
      exportedBy: "",
    });
  };

  const renderPaymentDetailPanel = () => {
    if (!selectedDetailItem) return null;

    const paymentSummary = getItemPaymentSummary(selectedDetailItem);
    const paymentCards = payments.map((profile, index) => {
      const sourceItemsById = new Map(
        paymentSummary.sourceItems.map((sourceItem) => [Number(sourceItem.itemId), sourceItem]),
      );
      const profilePayments = (profile.items || []).filter((payment) =>
        sourceItemsById.has(getPaymentTargetItemId(payment)),
      );

      if (profilePayments.length === 0) {
        return null;
      }

      const beforeTaxAmount = profilePayments.reduce((total, payment) => {
        const sourceItem = sourceItemsById.get(getPaymentTargetItemId(payment));
        return total + getPaymentAmount(sourceItem, payment);
      }, 0);
      const totalQuantity = profilePayments.reduce(
        (total, payment) => total + Number(payment.paymentQuantity ?? payment.PaymentQuantity ?? 0),
        0,
      );
      const vatRate = Number(profile?.vat ?? subcontract?.vat ?? 0) / 100;
      const afterTaxAmount = Math.round(beforeTaxAmount * (1 + vatRate));

      return (
        <Box
          key={profile.paymentProfileId}
          sx={{
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            p: 1.25,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.74rem", fontWeight: 700, color: "#111827" }}>
                Đợt thanh toán {index + 1}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={`${profilePayments.length} dòng`}
              sx={{
                height: 22,
                bgcolor: "rgba(11, 92, 171, 0.08)",
                color: "#0b5cab",
                "& .MuiChip-label": {
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.62rem",
                  fontWeight: 700,
                },
              }}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
            {renderCompactField("Trước thuế", formatCurrencyWithUnit(beforeTaxAmount), { money: true })}
            {renderCompactField("Khối lượng", formatQuantity(totalQuantity))}
            {renderCompactField("Sau thuế", formatCurrencyWithUnit(afterTaxAmount), { money: true })}
            {renderCompactField("Kỳ thanh toán", profile.paymentName || `Đợt ${index + 1}`)}
          </Box>
        </Box>
      );
    }).filter(Boolean);

    return (
      <Box
        sx={{
          minWidth: 0,
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          bgcolor: "#fff",
          alignSelf: "start",
          position: { xs: "static", xl: "sticky" },
          top: { xl: 8 },
          overflow: "hidden",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.25, p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.76rem", fontWeight: 750, color: "#0b5cab" }}>
              {selectedDetailItem.itemCode || "-"}
            </Typography>
            <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.88rem", fontWeight: 700, color: "#111827", mt: 0.5 }}>
              {selectedDetailItem.itemName || "-"}
            </Typography>
          </Box>
          <Tooltip title="Đóng">
            <IconButton size="small" onClick={handleCloseDetailPanel}>
              <Close sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ p: 1.5, bgcolor: "#f8fafc", display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 3, bgcolor: "#fff", p: 1.5 }}>
            <Typography sx={{ fontFamily: "Inter, sans-serif", fontSize: "0.74rem", fontWeight: 750, color: "#111827", mb: 1.25 }}>
              Thông tin thanh toán
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
              {renderCompactField("Lũy kế trước thuế", formatCurrencyWithUnit(paymentSummary.beforeTax), {
                strong: true,
                money: true,
              })}
              {renderCompactField("Tiến độ thanh toán", `${Math.round(paymentSummary.progress)}%`)}
              {renderCompactField("Lũy kế sau thuế", formatCurrencyWithUnit(paymentSummary.afterTax), {
                strong: true,
                money: true,
              })}
            </Box>
          </Box>

          {paymentCards.length === 0 ? (
            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: 2.5,
                p: 1.5,
                bgcolor: "#fff",
                textAlign: "center",
                fontFamily: "Inter, sans-serif",
                fontSize: "0.72rem",
                color: "#64748b",
              }}
            >
              Chưa có dữ liệu thanh toán cho dự toán này.
            </Box>
          ) : (
            <Stack spacing={1.25}>{paymentCards}</Stack>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ApprovalTimeline subcontract={subcontract} approvals={approvals} />
      <SectionCard
        title="Dự toán quyết định giao khoán"
        action={(
          <Stack direction="row" spacing={1}>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleImportFile} />
            {!hasAnyItem && <AppButton size="small" startIcon={<UploadFileOutlined />} onClick={openImportDialog}>Import excel</AppButton>}
            <AppButton size="small" startIcon={<DownloadOutlined />} disabled={!hasAnyItem} onClick={exportItems}>Export excel</AppButton>
            {false && <AppButton size="small" variant="contained" startIcon={<Add />} disabled={!canEdit} onClick={() => setDialog({ type: "item" })}>
              Thêm dự toán
            </AppButton>}
          </Stack>
        )}
      >
        {sections.length === 0 ? <Alert severity="info">Chưa có loại phân cấp giao khoán.</Alert> : (
          <>
            {!hasAnyItem && <Alert severity="info" sx={{ mb: 2 }}>Chưa có dự toán. Vui lòng import Excel lần đầu, sau đó mới có thể thêm thủ công.</Alert>}
            {/* <ApprovalTimeline subcontract={subcontract} approvals={approvals} /> */}
            <Tabs value={Math.min(sectionIndex, sections.length - 1)} onChange={(_, value) => { setSectionIndex(value); setScopeIndex(0); }} className="subcontract-nested-tabs" variant="scrollable">
              {sections.map((item) => <Tab label={item.sectionName} key={item.subcontractSectionId} />)}
            </Tabs>
            {scopes.length > 0 ? (
              <>
                <Tabs value={Math.min(scopeIndex, scopes.length - 1)} onChange={(_, value) => setScopeIndex(value)} className="subcontract-nested-tabs" variant="scrollable" sx={{ mt: 0 }}>
                  {scopes.map((item) => <Tab label={item.scopeName} key={item.subcontractItemScopeId} />)}
                </Tabs>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                    {!activeScope?.isEditable && !isCommuneTotal && <Chip size="small" label="Chỉ xem" />}
                    {hasAnyItem && !activeScopeHasItems && <Chip size="small" label="Dữ liệu tổng hợp tự động" />}
                  </Box>
                  {tableNotice && (
                    <Alert severity={tableNotice.severity} sx={{ mb: 1.5 }} onClose={() => setTableNotice(null)}>
                      {tableNotice.message}
                    </Alert>
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: selectedDetailItem && canViewPaymentInfo
                        ? { xs: "1fr", xl: "minmax(0, 1fr) 380px" }
                        : "1fr",
                      gap: 1.5,
                      alignItems: "start",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <DataTable
                        columns={columns}
                        data={displayedItems}
                        initialState={{
                          columnVisibility: {
                            priority: false,
                            status: false,
                            completionPercentage: false
                          },
                        }}
                        onExpandAll={() => setExpandedItemIds(new Set(collectExpandableItemIds(displayedItemTree)))}
                        onCollapseAll={() => setExpandedItemIds(new Set())}
                        enableEditing={canEdit}
                        editDisplayMode="cell"
                        enablePagination={false}
                        enableBottomToolbar={false}
                        state={{ showProgressBars: inlineSaving }}
                        muiTableBodyCellProps={({ cell, row }) => {
                          const canInlineEdit = isInlineCellEditable(row.original, cell.column.id);

                          return {
                            title: canInlineEdit ? "Double click để chỉnh sửa nhanh" : undefined,
                            sx: {
                              cursor: canInlineEdit ? "cell" : undefined,
                              "&:hover": canInlineEdit
                                ? {
                                  backgroundColor: "#fff7ed !important",
                                  outline: "1px solid #fb923c",
                                  outlineOffset: "-1px",
                                }
                                : undefined,
                            },
                          };
                        }}
                        muiTableBodyRowProps={({ row }) => {
                          const rowKey = getDetailItemKey(row.original);
                          const isSelected = rowKey === selectedDetailItemKey;

                          return {
                            sx: {
                              backgroundColor: isSelected
                                ? "#e8f1ff"
                                : row.original.level_ === 0
                                  ? "rgba(0, 0, 0, 0.035)"
                                  : "#ffffff",
                              "&:hover": {
                                backgroundColor: isSelected
                                  ? "#dbeafe"
                                  : row.original.level_ === 0
                                    ? "rgba(0, 0, 0, 0.06)"
                                    : "rgba(0, 0, 0, 0.04)",
                              },
                              "& .MuiTableCell-root": {
                                backgroundColor: isSelected
                                  ? "#eff6ff !important"
                                  : row.original.level_ === 0
                                    ? "#f8fafc !important"
                                    : "#ffffff !important",
                              },
                              "& .MuiTableCell-root:first-of-type": {
                                borderLeft: isSelected ? "3px solid #2563eb" : "3px solid transparent",
                              },
                            },
                          };
                        }}
                        enableExport
                        exportFileName={`hang-muc-${activeSection?.sectionType || "subcontract"}-${activeScope?.scopeType || "scope"}`}
                        enableRowActions={canEdit || canViewPaymentInfo}
                        renderRowActions={({ row }) => (
                          <Box>
                            {canViewPaymentInfo && (
                              <Tooltip title="Thông tin thanh toán">
                                <IconButton size="small" color="primary" onClick={() => handleOpenDetailPanel(row.original)}>
                                  <Payment sx={{ fontSize: "13px" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Chỉnh sửa">
                                <IconButton size="small" onClick={() => setDialog({ type: "item", data: row.original })}>
                                  <EditOutlined sx={{ fontSize: "13px" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && (
                              <Tooltip title="Xóa">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={async () => {
                                    try {
                                      setTableNotice(null);
                                      const response = await subcontractApi.deleteItem(row.original.itemId);
                                      if (response?.success === false || response?.Success === false) {
                                        setTableNotice({
                                          severity: "error",
                                          message: response?.message || response?.Message || "Không thể xóa dự toán giao khoán",
                                        });
                                        return;
                                      }
                                      await onChanged?.();
                                      setTableNotice({
                                        severity: "success",
                                        message: response?.message || response?.Message || "Xóa dự toán giao khoán thành công",
                                      });
                                    } catch (error) {
                                      setTableNotice({
                                        severity: "error",
                                        message:
                                          error?.response?.data?.message ||
                                          error?.response?.data?.Message ||
                                          error?.message ||
                                          "Không thể xóa dự toán giao khoán",
                                      });
                                    }
                                  }}
                                >
                                  <DeleteOutline sx={{ fontSize: "13px" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      />
                    </Box>

                    {canViewPaymentInfo && selectedDetailItem && renderPaymentDetailPanel()}
                  </Box>
                </Box>
              </>
            ) : <Alert severity="info" sx={{ mt: 2 }}>Loại dự toán này chưa có phân cấp dữ liệu.</Alert>}
          </>
        )}

        <EntityFormDialog
          open={dialog?.type === "item"}
          onClose={() => setDialog(null)}
          title={dialog?.data ? "Cập nhật dự toán giao khoán" : "Thêm dự toán giao khoán"}
          initialData={dialog?.data
            ? {
              ...dialog.data,
              startDate: formatInputDate(dialog.data.startDate),
              endDate: formatInputDate(dialog.data.endDate),
            }
            : { status: "Chưa bắt đầu", completionPercentage: 0, subcontractQuantity: 0, laborCount: null, laborNorm: null, laborPrice: null, priority: 2, difficultyLevel: 1 }}
          onSubmit={saveItem}
          fields={[
            { name: "itemCode", label: "STT", disabled: dialog?.data },
            { name: "parentItemId", label: "Dự toán cha", options: parentOptions, disabled: Boolean(dialog?.data) },
            { name: "itemName", label: "Dự toán", full: true, required: true },
            { name: "unit", label: "Đơn vị tính", options: UNITS },
            { name: "difficultyLevel", label: "Mức KK", options: DIFFICULTY_LEVELS },
            { name: "subcontractQuantity", label: "Khối lượng", type: "number", inputProps: { min: 0, step: "any" } },
            { name: "unitPrice", label: "Đơn giá", type: "number", disabled: true, inputProps: { min: 0, step: "any" } },
            { name: "laborCount", label: "Định biên", type: "number", inputProps: { min: 0, step: "any" } },
            { name: "laborNorm", label: "Định mức", type: "number", inputProps: { min: 0, step: "any" } },
            { name: "laborPrice", label: "Đơn giá công", type: "number", inputProps: { min: 0, step: "any" } },
            { name: "priority", label: "Độ ưu tiên", options: PRIORITIES },
            { name: "status", label: "Trạng thái", options: CONTRACT_ITEM_STATUSES },
            { name: "startDate", label: "Từ ngày", type: "date" },
            { name: "endDate", label: "Đến ngày", type: "date" },
            { name: "completionPercentage", label: "Tiến độ (%)", type: "number", inputProps: { min: 0, max: 100 }, disabled: true },
            { name: "notes", label: "Ghi chú", multiline: true, full: true },
          ]}
        />

        <Dialog open={importDialog.open} onClose={importDialog.checking || importDialog.importing ? undefined : () => setImportDialog((current) => ({ ...current, open: false }))} maxWidth="md" fullWidth>
          <DialogTitle>
            <Typography component="div" variant="h6">Import dự toán giao khoán</Typography>
            {importScopeNames.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, wordBreak: "break-word" }}>
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  Danh sách các tỉnh-xã/phân cấp:
                </Box>{" "}
                {importScopeNames.join(", ")}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="info">Import chỉ dùng khi quyết định giao khoán chưa có dự toán. Mỗi sheet dữ liệu phải mang đúng tên tỉnh-xã/phân cấp trong danh sách trên; các sheet Danh_muc_co_dinh và Phien_ban được bỏ qua.</Alert>
              {!provinceId && <Alert severity="warning">Quyết định giao khoán chưa có tỉnh/thành phố, không thể chọn xã/phường để import.</Alert>}
              {requiresCommuneSelection(sections) && selectedCommuneKeys.length === 0 && (
                <Alert severity="warning">Chưa chọn phường/xã.</Alert>
              )}
              {requiresCommuneSelection(sections) && (
                <Box>
                  <Typography fontWeight={700} sx={{ mb: 1 }}>Phạm vi xã</Typography>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={provinceId && communeOptions.length > 0 ? openCommunePicker : undefined}
                    onKeyDown={(event) => {
                      if ((event.key === "Enter" || event.key === " ") && provinceId && communeOptions.length > 0) {
                        event.preventDefault();
                        openCommunePicker(event);
                      }
                    }}
                    sx={(theme) => ({
                      minHeight: 44,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      px: 1.5,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                      cursor: provinceId && communeOptions.length > 0 ? "pointer" : "default",
                      bgcolor: theme.palette.background.paper,
                      "&:hover": {
                        borderColor: provinceId && communeOptions.length > 0 ? theme.palette.primary.main : theme.palette.divider,
                      },
                    })}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontSize={13} color={selectedCommuneNames.length ? "text.primary" : "text.secondary"} noWrap>
                        {selectedCommuneNames.length
                          ? `${selectedCommuneNames.length} xã/phường đã chọn`
                          : "Chọn xã/phường"}
                      </Typography>
                      {selectedCommuneNames.length > 0 && (
                        <Typography fontSize={12} color="text.secondary" noWrap>
                          {selectedCommuneNames.slice(0, 4).join(", ")}
                          {selectedCommuneNames.length > 4 ? `, +${selectedCommuneNames.length - 4}` : ""}
                        </Typography>
                      )}
                    </Box>
                    <ArrowDropDown color="action" />
                  </Box>
                  <Popover
                    open={Boolean(communePickerAnchor)}
                    anchorEl={communePickerAnchor}
                    onClose={closeCommunePicker}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{
                      sx: (theme) => ({
                        width: 340,
                        maxWidth: "calc(100vw - 32px)",
                        mt: 0.75,
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: theme.shadows[8],
                        overflow: "hidden",
                      }),
                    }}
                  >
                    <Box sx={{ p: 1.25 }}>
                      <TextField
                        size="small"
                        fullWidth
                        autoFocus
                        placeholder="Tìm xã/phường"
                        value={communeSearch}
                        onChange={(event) => setCommuneSearch(event.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchOutlined fontSize="small" color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                    <Box sx={{ minHeight: 170, maxHeight: 260, overflowY: "auto", px: 1 }}>
                      {filteredCommuneOptions.length === 0 ? (
                        <Box sx={{ minHeight: 150, display: "grid", placeItems: "center" }}>
                          <Typography color="text.secondary" fontSize={13}>Không có kết quả</Typography>
                        </Box>
                      ) : filteredCommuneOptions.map((commune) => {
                        const communeKey = String(commune.communeId);
                        const checked = communeDraftKeys.includes(communeKey);
                        return (
                          <Box
                            key={communeKey}
                            onClick={() => toggleCommuneDraft(communeKey)}
                            sx={(theme) => ({
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 0.75,
                              borderRadius: 1,
                              cursor: "pointer",
                              "&:hover": { bgcolor: theme.palette.action.hover },
                            })}
                          >
                            <Checkbox size="small" checked={checked} tabIndex={-1} disableRipple />
                            <Typography fontSize={13}>{commune.communeName}</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    <Divider />
                    <Stack direction="row" spacing={1.5} sx={{ p: 1.25 }}>
                      <AppButton variant="outlined" size="small" onClick={applyCommuneSelection} sx={{ minWidth: 104 }}>
                        Chọn xã/phường
                      </AppButton>
                      <AppButton color="inherit" size="small" onClick={resetCommuneSelection} sx={{ minWidth: 96 }}>
                        Xóa tất cả
                      </AppButton>
                    </Stack>
                  </Popover>
                  {provinceId && communeOptions.length === 0 && <Alert severity="warning" sx={{ mt: 1 }}>Không tìm thấy xã/phường thuộc tỉnh của quyết định giao khoán.</Alert>}
                </Box>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1.4fr" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 0.5 }}
                  >
                    1. Tải file mẫu
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Dùng đúng cấu trúc file mẫu và đổi tên từng sheet dữ liệu theo danh sách được chấp nhận.
                  </Typography>
                  <AppButton
                    component="a"
                    href={templateFileUrl}
                    download="mau_import_hang_muc_hop_dong_giao_khoan.xlsx"
                    variant="outlined"
                    startIcon={<FileDownload />}
                    sx={{
                      textTransform: "none",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Tải file mẫu
                  </AppButton>
                </Box>

                <Box
                  sx={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontFamily: "Inter, sans-serif", mb: 1 }}
                  >
                    2. Chọn file và kiểm tra
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <AppButton startIcon={<UploadFileOutlined />} onClick={() => fileInputRef.current?.click()} disabled={importDialog.checking || importDialog.importing}>
                      Chọn file Excel
                    </AppButton>
                    <AppButton variant="contained" onClick={checkImportFile} disabled={!importDialog.file || importDialog.checking || importDialog.importing}>
                      {importDialog.checking ? "Đang kiểm tra..." : "Kiểm tra dữ liệu"}
                    </AppButton>
                    {importDialog.fileName && <Typography component="span">{importDialog.fileName}</Typography>}
                  </Box>
                </Box>
              </Box>
              {importDialog.error && <Alert severity="error">{importDialog.error}</Alert>}
              {importDialog.result?.errors?.length > 0 && (
                <DataTable
                  columns={[
                    { accessorKey: "sheetName", header: "Sheet", size: 140 },
                    { accessorKey: "rowNumber", header: "Dòng", size: 70 },
                    { accessorKey: "importCode", header: "Mã dự toán", size: 100 },
                    { accessorKey: "column", header: "Cột", size: 140 },
                    {
                      accessorKey: "value",
                      header: "Giá trị hiện tại",
                      size: 180,
                      Cell: ({ cell }) => formatImportErrorValue(cell.getValue()),
                    },
                    { accessorKey: "message", header: "Mô tả lỗi", size: 360 },
                  ]}
                  enableExpanding
                  data={importDialog.result.errors.map((error, index) => ({ id: index, ...error }))}
                  enableRowActions={false}
                  enableExport={false}
                />
              )}
              {importDialog.result?.isValid && (
                <Alert severity="success">File hợp lệ, sẵn sàng import {importDialog.result.items.length} dự toán từ {importDialog.result.scopes.length} sheet.</Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <AppButton color="inherit" disabled={importDialog.checking || importDialog.importing} onClick={() => setImportDialog((current) => ({ ...current, open: false }))}>Hủy</AppButton>
            <AppButton variant="contained" disabled={importDialog.checking || importDialog.importing || !importDialog.result?.isValid || (requiresCommuneSelection(sections) && selectedCommuneKeys.length === 0)} onClick={submitImport}>
              {importDialog.importing ? "Đang import..." : "Import"}
            </AppButton>
          </DialogActions>
        </Dialog>
      </SectionCard>
    </>
  );
}
